// src/stores/auth.js

import { defineStore } from "pinia";
import { ref, computed } from "vue";
import router from "@/router"; // Assuming your router is defined here
import axios from "axios"; // Assuming axios is installed and used for HTTP requests
import { jwtDecode } from "jwt-decode";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
// For Vue CLI, environment variables should be prefixed with VUE_APP_
const BACKEND_BASE_URL =
  process.env.VUE_APP_BACKEND_BASE_URL || "http://localhost:3000";
const TOKEN_STORAGE_KEY = "backendJwtToken";

// Main Pinia Store
export const useAuthStore = defineStore("auth", () => {
  // --- State ---
  // The 'user' object will now solely contain data from your backend
  const user = ref(null);
  const alumni = ref(null);
  const loading = ref(true); // General loading state for auth operations
  const error = ref(null);
  const authInitialized = ref(false); // Indicates if initial auth check is complete
  const backendJwtToken = ref(localStorage.getItem(TOKEN_STORAGE_KEY) || null);
  const isAdmin = ref(false); // Determined directly from backend user data
  const editAccount = ref(false);
  const isEditing = ref(false);
  // UI States (if you want to manage them here)
  const drawer = ref(false);
  const rail = ref(false);
  const loginAttemp = ref(0);

  // const snackbar = ref(false);
  // const snackbarMessage = ref("");
  // const snackbarColor = ref("success");
  // --- Getters ---
  const isAuthenticated = computed(
    () => !!user.value && !!backendJwtToken.value,
  );
  const userRole = computed(() => getRoleFromToken());
  const userName = computed(() => user.value?.name || user.value?.stdName);
  const userEmail = computed(() => user.value?.email);
  const userPhoto = computed(() => user.value?.photoURL); // If your backend provides a photo URL
  // const userRole = computed(() => user.value?.role || "guest");
  const user_drive_id = computed(() => user.value?.drive_id || "no drive ID"); // If your backend provides this
  // const drawer = computed(() => !!isAuthenticated.value);
  // const rail = computed(() => !!isAuthenticated.value);

  // --- Backend JWT Management Functions ---
  const storeBackendJwt = (token) => {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
    backendJwtToken.value = token; // Update reactive state
    console.log("JWT successfully stored:", token);
  };

  const getBackendJwt = () => {
    return backendJwtToken.value; // Return the reactive token
  };

  const clearBackendJwt = () => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    backendJwtToken.value = null;
  };

  // --- User State Management Helper Function ---
  // This function now expects user data directly from your backend API
  const updateFrontendUserState = (backendUserData = {}) => {
    if (backendUserData.Role === "alumni") {
      user.value = {
        studentNum: backendUserData.studentNum,
        firstname: backendUserData.firstname,
        lastname: backendUserData.lastname,
        email: backendUserData.email,
        LineId: backendUserData.LineId,
        address: backendUserData.address,
        province: backendUserData.province,
        district: backendUserData.district,
        subDistrict: backendUserData.subDistrict,
        zipCode: backendUserData.zipCode,
        jobName: backendUserData.jobName,
        jobPhone: backendUserData.jobPhone,
        jobAddress: backendUserData.jobAddress,
        status: backendUserData.status,
        //   ...(backendUserData.newPhone && {
        //     currentPhone: backendUserData.currentPhone,
        //     newPhone: backendUserData.newPhone,
      };
    } else {
      user.value = {
        id: backendUserData._id,
        email: backendUserData.email,
        displayName:
          backendUserData.displayName ||
          backendUserData.name ||
          backendUserData.email?.split("@")[0],
        displayNameEn: backendUserData.displayNameEn,
        studentNum: backendUserData.studentNum,
        createdAt: backendUserData.createdAt,
        lastLogin: backendUserData.lastLogin,
        // drive_id: backendUserData.drive_id,
        isDefaultPassword: backendUserData.isDefaultPassword,
        PLO: backendUserData.PLO,
        Academic_results: backendUserData.Academic_results,
      };
    }
  };

  const clearUserState = () => {
    console.log("Clearing user state and backend JWT...");
    user.value = null;
    clearBackendJwt(); // Only clear the backend JWT
  };

  // --- Main Authentication Actions ---

  // const logout = async () => {
  //   loading.value = true;
  //   error.value = null;
  //   const storedToken = getBackendJwt();
  //   try {
  //     const response = await axios.post(
  //       `${BACKEND_BASE_URL}/logout`,
  //       {}, // Correctly placed empty body
  //       {
  //         headers: {
  //           Authorization: `Bearer ${storedToken}`,
  //           "Content-Type": "application/json",
  //         },
  //       }
  //     );
  //     if (response.status === 200) {
  //       clearUserState(); // Clears all user-related state and JWT
  //       router.push("/");
  //       drawer.value = false;
  //       rail.value = false;
  //     }
  //   } catch (err) {
  //     error.value = err.message;
  //     console.error("Error signing out:", err);
  //     throw err;
  //   } finally {
  //     loading.value = false;
  //   }
  // };

  const logout = async () => {
    loading.value = true;
    error.value = null;

    try {
      clearUserState(); // Clears all user-related state and JWT
      router.push("/");
      drawer.value = false;
      rail.value = false;
    } catch (err) {
      error.value = err.message;
      console.error("Error signing out:", err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Validates the stored backend JWT token with your backend API.
   * This is crucial for maintaining session across page reloads.
   * @param {string} token - The JWT token to validate.
   * @returns {Promise<boolean>} - True if token is valid, false otherwise.
   */
  const validateBackendToken = async (token) => {
    if (!token) {
      console.log("No backend token found to validate.");
      clearUserState();
      return false;
    }
    try {
      console.log("Validating backend token with API...", token);
      const response = await axios.get(`/api/validate-token`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (
        (response.status === 200 && response.data.student) ||
        response.data.teacher ||
        response.data.alumni
      ) {
        console.log(
          "validated debug",
          response.data.student ||
            response.data.teacher ||
            response.data.alumni,
        );
        // Only save res.data.student so if want to pass data to auth save it in student
        updateFrontendUserState(
          response.data.student ||
            response.data.teacher ||
            response.data.alumni,
        );
        console.log("Backend token validated, user restored:", response);
        return true;
      } else {
        // Token is invalid or backend didn't return user data, clear it
        console.warn(
          "Backend token validation failed or no user data:",
          response.data,
        );
        clearUserState();
        return false;
      }
    } catch (error) {
      console.error(
        "Error validating backend token:",
        error.response?.data?.message || error.message,
      );
      clearUserState(); // Clear token on validation error
      return false;
    }
  };

  /**
   * Initializes the authentication state when the app starts.
   * It checks for an existing JWT and validates it.
   */
  const initializeAuth = async () => {
    if (authInitialized.value) {
      console.log("DEBUG already have user.value", user.value);
      return user.value; // Already initialized, return current user
    }

    loading.value = true;
    error.value = null;
    console.log("Initializing authentication...");
    try {
      const storedToken = getBackendJwt();
      console.log("Found stored token during initialization:", storedToken);
      if (storedToken) {
        await validateBackendToken(storedToken);
        console.log("checking token");
      } else {
        clearUserState(); // No token, ensure state is clean
        console.log(
          "No token found during initialization, user not authenticated.",
        );
      }
    } catch (err) {
      console.error("Error during authentication initialization:", err);
      clearUserState(); // Ensure state is clean on error
    } finally {
      loading.value = false;
      authInitialized.value = true; // Mark as initialized regardless of success
      drawer.value = true;
      rail.value = true;
      console.log(
        " Finally block : Authentication initialization complete.",
        user.value,
      );
      return user.value;
    }
  };

  const getRoleFromToken = () => {
    const token = getBackendJwt();
    if (token) {
      try {
        const decoded = jwtDecode(token);
        return decoded.Role;
      } catch (err) {
        console.log("Token decode error:", err);
        return null;
      }
    }
    console.log("No token found");
    return null;
  };

  async function createPdf(displayName, lang, studentNum) {
    let url = ``;
    if (lang === "th") {
      console.log("option", url);
      url = `/cert_th.pdf`;
    } else if (lang === "en") {
      console.log("option", url);
      url = `/cert.pdf`;
    }

    // const url = `/cert.pdf`;
    const fontUrl = `/THSarabun.ttf`; // actualyy sarabunPSK
    loading.value = true;
    try {
      const [pdfResponse, fontResponse] = await Promise.all([
        fetch(url, {
          headers: {
            "Cache-Control": "no-cache",
            "X-Requested-With": "XMLHttpRequest",
          },
        }),
        fetch(fontUrl, {
          headers: {
            "Cache-Control": "no-cache",
            "X-Requested-With": "XMLHttpRequest",
          },
        }),
      ]);

      if (!pdfResponse.ok) {
        throw new Error(`PDF fetch failed: ${pdfResponse.status}`);
      }

      if (!fontResponse.ok) {
        throw new Error(`Font fetch failed: ${fontResponse.status}`);
      }

      const [existingPdfBytes, fontBytes] = await Promise.all([
        pdfResponse.arrayBuffer(),
        fontResponse.arrayBuffer(),
      ]);

      const pdfDoc = await PDFDocument.load(existingPdfBytes);

      const fontkit = await import("@pdf-lib/fontkit");
      pdfDoc.registerFontkit(fontkit.default || fontkit);
      pdfDoc.setTitle("ใบประกาศนียบัตร");
      const customFont = await pdfDoc.embedFont(fontBytes);
      const Name = displayName.normalize("NFC");
      const sNum = studentNum;

      const pages = pdfDoc.getPages();
      const firstPage = pages[0];
      const { width, height } = firstPage.getSize();

      // 1. Define the font size for the name
      const fontSize = 20;

      // 2. Calculate the width of the text using the font and size
      const textWidth = customFont.widthOfTextAtSize(Name, fontSize);

      // 3. Calculate the new x-coordinate to center the text
      // const textX = (width - textWidth) / 2;

      if (lang === "th") {
        firstPage.drawText(sNum, {
          x: 125, // Use the new, calculated X coordinate
          y: 613,
          size: fontSize,
          font: customFont,
          color: rgb(0, 0, 0),
        });

        firstPage.drawText(Name, {
          x: 125, // Use the new, calculated X coordinate
          y: 595,
          size: fontSize,
          font: customFont,
          color: rgb(0, 0, 0),
        });
      } else if (lang === "en") {
        firstPage.drawText(sNum, {
          x: 125, // Use the new, calculated X coordinate
          y: 618,
          size: fontSize,
          font: customFont,
          color: rgb(0, 0, 0),
        });

        firstPage.drawText(Name, {
          x: 125, // Use the new, calculated X coordinate
          y: 598,
          size: fontSize,
          font: customFont,
          color: rgb(0, 0, 0),
        });
      }

      const pdfBytes = await pdfDoc.save();
      const pdfBlob = new Blob([pdfBytes], { type: "application/pdf" });
      const pdfUrl = URL.createObjectURL(pdfBlob);

      // Open in new tab
      window.open(pdfUrl, "_blank");

      // Create download link with custom filename
      const downloadLink = document.createElement("a");
      //downloadLink.href = pdfUrl;
      downloadLink.download = `${Name}-certificate.pdf`; // Custom filename
      downloadLink.style.display = "none";

      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      setTimeout(() => {
        URL.revokeObjectURL(pdfUrl);
      }, 1000);
    } catch (error) {
      console.error("Error:", error.message);
    } finally {
      loading.value = false;
    }
  }

  // --- Return all state, getters, and actions ---
  return {
    // State
    user,
    alumni,
    loading,
    error,
    authInitialized,
    isAdmin,
    drawer,
    rail,
    backendJwtToken,
    editAccount,
    isEditing,
    loginAttemp,
    // Getters
    isAuthenticated,
    userName,
    userEmail,
    userPhoto,
    userRole,

    user_drive_id,
    createPdf,
    getBackendJwt,
    getRoleFromToken,
    // Actions
    logout,
    initializeAuth,
    validateBackendToken, // Exposed for potential manual validation if needed
    clearBackendJwt, // Exposed for explicit clearing
    storeBackendJwt,
  };
});
